import { Close as CloseIcon } from "@mui/icons-material";
import { Box, Tabs, Tab, IconButton } from "@mui/material";
import { ReactNode, useState } from "react";

export interface DetailTab {
  label: string;
  content: ReactNode;
}

interface DetailDrawerProps {
  open: boolean;
  onClose: () => void;
  tabs: DetailTab[];
}

export function DetailDrawer({ open, onClose, tabs }: DetailDrawerProps) {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box
      sx={{
        height: open ? "40vh" : "0",
        maxHeight: open ? "500px" : "0",
        minHeight: open ? "300px" : "0",
        overflow: "hidden",
        transition: "height 0.3s ease-in-out, min-height 0.3s ease-in-out",
        boxShadow: open ? "0 -4px 20px rgba(0, 0, 0, 0.1)" : "none",
        borderTop: open ? (theme) => `1px solid ${theme.palette.divider}` : "none",
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        mt: 0,
      }}
    >
      <Box
        px={2}
        pt={1}
        pb={0}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, index) => setActiveTab(index)}
          sx={{
            flex: 1,
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              fontFamily: "var(--font-family-halyard-text)",
              py: 1,
            },
          }}
        >
          {tabs.map((tab, idx) => (
            <Tab key={idx} label={tab.label} />
          ))}
        </Tabs>
        <IconButton
          onClick={onClose}
          role="button"
          aria-label="Close drawer"
          sx={{
            ml: 2,
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        p={2}
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          height: "calc(100% - 52px)",
          position: "relative",
          "& .MuiTypography-root": {
            fontSize: "0.875rem",
            lineHeight: 1.5,
          },
          "& > .MuiCircularProgress-root": {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          },
        }}
      >
        {tabs[activeTab]?.content}
      </Box>
    </Box>
  );
}
